import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import Button from 'antd/lib/button';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import { Flex } from 'antd/lib';
import { DeleteOutlined } from '@ant-design/icons';
import * as React from 'react';
import { formItemHelpStyle, runwayColors } from '@/components/styles/globalStyles';

export const CUSTOM_CI_TRIGGER_HEADERS_FIELD = 'triggerHeaders';

export interface CustomCITriggerHeaderRow {
    headerKey: string;
    headerValue: string;
}

// Builds the Form.List initial value from the redacted keys returned by the backend. Values are
// secret, so existing headers start blank and are shown with a redacted placeholder.
export const initialTriggerHeaderRows = (triggerHeaderKeys?: string[]): CustomCITriggerHeaderRow[] =>
    (triggerHeaderKeys ?? []).map((headerKey) => ({ headerKey, headerValue: '' }));

interface Props {
    // Keys of headers already stored on the installation; their values are redacted in the UI.
    redactedHeaderKeys: string[];
}

// A key/value editor for the Custom CI trigger's HTTP headers. Rendered inside the integration
// config Form via a FieldType.Custom field, so it reads the surrounding form via useFormInstance.
// The initial rows are seeded by the parent Form's initialValues (keyed by CUSTOM_CI_TRIGGER_HEADERS_FIELD).
const CustomCITriggerHeadersEditor: React.FC<Props> = ({ redactedHeaderKeys }) => {
    const form = Form.useFormInstance();
    const redactedKeys = React.useMemo(() => new Set(redactedHeaderKeys), [redactedHeaderKeys]);

    return (
        <Form.List name={CUSTOM_CI_TRIGGER_HEADERS_FIELD}>
            {(fields, { add, remove }) => (
                <Space direction="vertical">
                    <Text>Custom headers</Text>
                    <Space direction="vertical">
                        {fields.map((field) => {
                            const rows: CustomCITriggerHeaderRow[] = form.getFieldValue(
                                CUSTOM_CI_TRIGGER_HEADERS_FIELD,
                            );
                            const headerKey = rows?.[field.name]?.headerKey;
                            const isRedacted = redactedKeys.has(headerKey);
                            return (
                                <Flex key={field.key}>
                                    <Space direction="horizontal" align="start">
                                        <Form.Item
                                            {...field}
                                            rules={[{ required: true, message: 'Key is required' }]}
                                            name={[field.name, 'headerKey']}
                                            style={{ marginBottom: 0 }}
                                        >
                                            <Input />
                                        </Form.Item>
                                        <Form.Item
                                            {...field}
                                            rules={[{ required: !isRedacted, message: 'Value is required' }]}
                                            name={[field.name, 'headerValue']}
                                            style={{ marginBottom: 0 }}
                                        >
                                            <Input
                                                className={isRedacted ? 'redacted-input' : ''}
                                                placeholder={isRedacted ? '••••••••••••' : undefined}
                                            />
                                        </Form.Item>
                                    </Space>
                                    <Button
                                        icon={<DeleteOutlined style={{ color: runwayColors.statusRed }} />}
                                        danger
                                        type="link"
                                        onClick={() => remove(field.name)}
                                    />
                                </Flex>
                            );
                        })}
                    </Space>
                    <Button onClick={() => add()}>Add custom header</Button>
                    <Text type="secondary" style={formItemHelpStyle}>
                        All values are automatically redacted for security. To update these headers, please re-enter the
                        full value.
                    </Text>
                </Space>
            )}
        </Form.List>
    );
};

export default CustomCITriggerHeadersEditor;
